// Lightweight rich-text editor for the internal-mail Compose window.
//
// Built on a plain contentEditable surface + a compact formatting toolbar so we
// avoid a heavyweight editor dependency. Supports bold / italic / underline /
// strikethrough, font colour, bullet & numbered lists, clearing formatting, and
// inserting / pasting images (auto-downscaled to keep the stored HTML small).
//
// The value is HTML. It is sanitised again on render (see MessageViewerDialog),
// so this component only needs to produce reasonable markup.
import React, { useCallback, useEffect, useRef } from 'react';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, Image as ImageIcon, RemoveFormatting, Palette,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { imageToInlineDataUrl, isImageFile } from '@/lib/mail';
import { toast } from 'sonner';

const SWATCHES = [
  '#0F172A', '#334155', '#64748B', '#B91C1C', '#DC2626', '#EA580C',
  '#CA8A04', '#16A34A', '#0891B2', '#2563EB', '#7C3AED', '#DB2777',
];

export const RichTextEditor = ({
  value = '',
  onChange,
  placeholder = 'Write your message…',
  disabled = false,
  testId = 'rich-text-editor',
  minHeight = 200,
}) => {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const savedRange = useRef(null);

  // Push external value into the DOM only when it diverges from what the editor
  // already shows — prevents the caret from jumping while the user types.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if ((value || '') !== el.innerHTML) {
      el.innerHTML = value || '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = useCallback(() => {
    const el = ref.current;
    if (el && onChange) onChange(el.innerHTML);
  }, [onChange]);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && ref.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0);
    }
  };

  const restoreSelection = () => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (savedRange.current && sel) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  const exec = (command, arg = null) => {
    restoreSelection();
    try {
      document.execCommand('styleWithCSS', false, true);
      document.execCommand(command, false, arg);
    } catch { /* noop */ }
    saveSelection();
    emit();
  };

  const insertImageDataUrl = (dataUrl) => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const img = document.createElement('img');
    img.src = dataUrl;
    img.alt = 'image';
    img.setAttribute('style', 'max-width:100%;height:auto;border-radius:6px;margin:4px 0;');

    const sel = window.getSelection();
    let range = null;
    if (savedRange.current && el.contains(savedRange.current.commonAncestorContainer)) {
      range = savedRange.current;
    } else if (sel && sel.rangeCount && el.contains(sel.anchorNode)) {
      range = sel.getRangeAt(0);
    }
    try {
      if (range) {
        range.collapse(false); // insert at caret / end of selection (never replace)
        range.insertNode(img);
        range.setStartAfter(img);
        range.collapse(true);
        if (sel) { sel.removeAllRanges(); sel.addRange(range); }
      } else {
        el.appendChild(img);
      }
    } catch {
      el.appendChild(img);
    }
    saveSelection();
    emit();
  };

  const handleImageFiles = async (files) => {
    const imgs = Array.from(files || []).filter((f) => isImageFile(f));
    if (imgs.length === 0) return;
    for (const f of imgs) {
      try {
        const dataUrl = await imageToInlineDataUrl(f, { maxDim: 1280, quality: 0.82 });
        insertImageDataUrl(dataUrl);
      } catch {
        toast.error('Could not add image');
      }
    }
  };

  const onPickImage = async (e) => {
    // Snapshot to a real array BEFORE clearing the input — resetting value
    // empties the live FileList, which would otherwise drop the selection.
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    await handleImageFiles(files);
  };

  const onPaste = async (e) => {
    const cd = e.clipboardData;
    if (!cd) return;
    const imageItems = Array.from(cd.items || []).filter((it) => it.type && it.type.startsWith('image/'));
    if (imageItems.length > 0) {
      e.preventDefault();
      saveSelection();
      const files = imageItems.map((it) => it.getAsFile()).filter(Boolean);
      await handleImageFiles(files);
      return;
    }
    // Plain-text paste: insert as text to avoid pulling in external markup.
    const text = cd.getData('text/plain');
    if (text && !cd.getData('text/html')) {
      e.preventDefault();
      try { document.execCommand('insertText', false, text); } catch { /* noop */ }
      emit();
    }
    // Otherwise allow the browser's default HTML paste (sanitised on render).
  };

  const btn = 'inline-flex items-center justify-center h-7 w-7 rounded text-slate-600 hover:bg-slate-200/70 hover:text-[var(--ink-navy)] transition-colors disabled:opacity-40 disabled:pointer-events-none';

  return (
    <div className="rounded-md border border-[var(--divider)] bg-white overflow-hidden focus-within:ring-2 focus-within:ring-[var(--ink-navy)]/20" data-testid={testId}>
      {/* Toolbar */}
      <div className="flex items-center flex-wrap gap-0.5 px-1.5 py-1 border-b border-[var(--divider)] bg-slate-50">
        <button type="button" className={btn} title="Bold" aria-label="Bold" disabled={disabled}
          onMouseDown={(e) => e.preventDefault()} onClick={() => exec('bold')} data-testid={`${testId}-bold`}>
          <Bold size={15} />
        </button>
        <button type="button" className={btn} title="Italic" aria-label="Italic" disabled={disabled}
          onMouseDown={(e) => e.preventDefault()} onClick={() => exec('italic')} data-testid={`${testId}-italic`}>
          <Italic size={15} />
        </button>
        <button type="button" className={btn} title="Underline" aria-label="Underline" disabled={disabled}
          onMouseDown={(e) => e.preventDefault()} onClick={() => exec('underline')} data-testid={`${testId}-underline`}>
          <UnderlineIcon size={15} />
        </button>
        <button type="button" className={btn} title="Strikethrough" aria-label="Strikethrough" disabled={disabled}
          onMouseDown={(e) => e.preventDefault()} onClick={() => exec('strikeThrough')} data-testid={`${testId}-strike`}>
          <Strikethrough size={15} />
        </button>

        <span className="mx-1 h-5 w-px bg-[var(--divider)]" />

        {/* Font colour */}
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className={btn} title="Font colour" aria-label="Font colour" disabled={disabled}
              onMouseDown={(e) => { e.preventDefault(); saveSelection(); }} data-testid={`${testId}-color`}>
              <Palette size={15} />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="start" data-testid={`${testId}-color-popover`}>
            <div className="grid grid-cols-6 gap-1.5">
              {SWATCHES.map((c) => (
                <button
                  key={c}
                  type="button"
                  className="h-6 w-6 rounded-full border border-black/10 hover:scale-110 transition-transform"
                  style={{ backgroundColor: c }}
                  title={c}
                  aria-label={`Colour ${c}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => exec('foreColor', c)}
                  data-testid={`${testId}-color-${c.replace('#', '')}`}
                />
              ))}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <input
                type="color"
                className="h-7 w-7 cursor-pointer rounded border border-[var(--divider)] bg-white p-0"
                onMouseDown={(e) => e.stopPropagation()}
                onChange={(e) => exec('foreColor', e.target.value)}
                data-testid={`${testId}-color-custom`}
                aria-label="Custom colour"
              />
              <span className="text-[11px] text-slate-500">Custom colour</span>
            </div>
          </PopoverContent>
        </Popover>

        <span className="mx-1 h-5 w-px bg-[var(--divider)]" />

        <button type="button" className={btn} title="Bulleted list" aria-label="Bulleted list" disabled={disabled}
          onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertUnorderedList')} data-testid={`${testId}-ul`}>
          <List size={15} />
        </button>
        <button type="button" className={btn} title="Numbered list" aria-label="Numbered list" disabled={disabled}
          onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertOrderedList')} data-testid={`${testId}-ol`}>
          <ListOrdered size={15} />
        </button>

        <span className="mx-1 h-5 w-px bg-[var(--divider)]" />

        <button type="button" className={btn} title="Insert image" aria-label="Insert image" disabled={disabled}
          onMouseDown={(e) => { e.preventDefault(); saveSelection(); }} onClick={() => fileRef.current?.click()} data-testid={`${testId}-image`}>
          <ImageIcon size={15} />
        </button>
        <button type="button" className={btn} title="Clear formatting" aria-label="Clear formatting" disabled={disabled}
          onMouseDown={(e) => e.preventDefault()} onClick={() => exec('removeFormat')} data-testid={`${testId}-clear`}>
          <RemoveFormatting size={15} />
        </button>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={onPickImage} data-testid={`${testId}-image-input`} />
      </div>

      {/* Editable surface */}
      <div
        ref={ref}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="Message body"
        data-placeholder={placeholder}
        onInput={emit}
        onBlur={saveSelection}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        onPaste={onPaste}
        className="mail-rte-surface px-3 py-2.5 text-sm text-slate-800 outline-none overflow-y-auto"
        style={{ minHeight, maxHeight: 380 }}
        data-testid={`${testId}-surface`}
      />
    </div>
  );
};

export default RichTextEditor;
